import React, { useState, useEffect } from 'react';
import { FaUniversity, FaEdit, FaTrash, FaEye, FaPlus, FaSearch, FaSortAmountDown, FaSortAmountUp, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axios from '../api/axios';

const StatCard = ({ title, value, icon, color }) => (
  <div className={`bg-gradient-to-r ${color} text-white p-6 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105`}>
    <div className="flex justify-between items-center">
      <div>
        <p className="text-sm">{title}</p>
        <h3 className="text-3xl font-bold">{value}</h3>
      </div>
      <div className="text-4xl opacity-80">{icon}</div>
    </div>
  </div>
);

export default function TipoTable() {
  const [tipos, setTipos] = useState([]);
  const [filteredTipos, setFilteredTipos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState('id');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    fetchTipos();
  }, []);

  useEffect(() => {
    filterAndSortTipos();
  }, [searchTerm, tipos, sortColumn, sortDirection]);

  const fetchTipos = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get('/tipos');
      setTipos(response.data);
      setFilteredTipos(response.data);
    } catch (err) {
      setError('Error al cargar los tipos. Por favor, intente de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const filterAndSortTipos = () => {
    let filtered = tipos.filter(tipo =>
      tipo.descripcion.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sorted = filtered.sort((a, b) => {
      if (a[sortColumn] < b[sortColumn]) return sortDirection === 'asc' ? -1 : 1;
      if (a[sortColumn] > b[sortColumn]) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredTipos(sorted);
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    setSortColumn(column);
  };

  const handleDeleteTipo = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este tipo?')) {
      setIsLoading(true);
      setError(null);
      try {
        await axios.delete(`/tipos/${id}`);
        fetchTipos();
      } catch (err) {
        setError('Error al eliminar el tipo. Por favor, intente de nuevo.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTipos.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(filteredTipos.length / itemsPerPage);

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold mb-8 text-center text-gray-800 font-serif">Gestión de Tipos</h1>
      <div className="flex justify-center mb-8">
        <StatCard title="Total tipos" value={tipos.length} icon={<FaUniversity />} color="from-green-500 to-green-700" />
      </div>
      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
        <div className="p-4 flex justify-between items-center bg-gray-50">
          <h2 className="text-xl font-semibold">Tabla de Tipos</h2>
          <Link to="/home/tipos/add" className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition">
            <FaPlus className="mr-2" /> Añadir tipo
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-blue-500 text-white">
              <tr>
                <th className="px-6 py-3">ID</th>
                <th className="px-6 py-3">Descripción</th>
                <th className="px-6 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.map((tipo) => (
                <tr key={tipo.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">{tipo.id}</td>
                  <td className="px-6 py-4">{tipo.descripcion}</td>
                  <td className="px-6 py-4 flex space-x-2">
                    <Link to={`/home/tipos/${tipo.id}`} className="text-blue-600 hover:text-blue-900"><FaEye /></Link>
                    <Link to={`/home/tipos/edit/${tipo.id}`} className="text-green-600 hover:text-green-900"><FaEdit /></Link>
                    <button onClick={() => handleDeleteTipo(tipo.id)} className="text-red-600 hover:text-red-900"><FaTrash /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-center space-x-2 p-4">
          {[...Array(totalPages).keys()].map((num) => (
            <button key={num + 1} onClick={() => setCurrentPage(num + 1)} className={`px-3 py-1 rounded ${currentPage === num + 1 ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}>{num + 1}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

/*
import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrash, FaEye, FaPlus, FaSearch, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axios from '../api/axios';

export default function TipoTable() {
  const [tipos, setTipos] = useState([]);
  const [filteredTipos, setFilteredTipos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState('id');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    fetchTipos();
  }, []);

  useEffect(() => {
    filterAndSortTipos();
  }, [searchTerm, tipos, sortColumn, sortDirection]);

  const fetchTipos = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get('/tipos');
      setTipos(response.data);
      setFilteredTipos(response.data);
    } catch (err) {
      setError('Error al cargar los tipos. Por favor, intente de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const filterAndSortTipos = () => {
    let filtered = tipos.filter(tipo =>
      tipo.descripcion.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sorted = filtered.sort((a, b) => {
      if (a[sortColumn] < b[sortColumn]) return sortDirection === 'asc' ? -1 : 1;
      if (a[sortColumn] > b[sortColumn]) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredTipos(sorted);
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    setSortColumn(column);
  };

  const handleDeleteTipo = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este tipo?')) {
      setIsLoading(true);
      setError(null);
      try {
        await axios.delete(`/tipos/${id}`);
        fetchTipos();
      } catch (err) {
        setError('Error al eliminar el tipo. Por favor, intente de nuevo.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTipos.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold mb-8 text-center text-gray-800 font-serif">Gestión de Tipos</h1>
      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
        <div className="p-4 flex justify-between items-center bg-gray-50">
          <h2 className="text-xl font-semibold">Tabla de Tipos</h2>
          <Link to="/home/tipos/add" className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition">
            <FaPlus className="mr-2" /> Añadir Tipo
          </Link>
        </div>
        <div className="p-4 flex items-center bg-gray-100">
          <FaSearch className="text-green-700 mr-2" />
          <input
            type="text"
            placeholder="Buscar por Descripción"
            className="border p-2 rounded w-64"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-blue-500 text-white">
              <tr>
                <th className="px-6 py-3 cursor-pointer" onClick={() => handleSort('id')}>ID {sortColumn === 'id' && (sortDirection === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />)}</th>
                <th className="px-6 py-3 cursor-pointer" onClick={() => handleSort('descripcion')}>Descripción {sortColumn === 'descripcion' && (sortDirection === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />)}</th>
                <th className="px-6 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.map((tipo) => (
                <tr key={tipo.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">{tipo.id}</td>
                  <td className="px-6 py-4">{tipo.descripcion}</td>
                  <td className="px-6 py-4 flex space-x-2">
                    <Link to={`/home/tipos/${tipo.id}`} className="text-blue-600 hover:text-blue-900"><FaEye /></Link>
                    <Link to={`/home/tipos/edit/${tipo.id}`} className="text-green-600 hover:text-green-900"><FaEdit /></Link>
                    <button onClick={() => handleDeleteTipo(tipo.id)} className="text-red-600 hover:text-red-900"><FaTrash /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
*/

/*import React, { useEffect, useState } from 'react';
import axios from 'axios';

const TipoList = () => {
  const [tipos, setTipos] = useState([]);
  const [search, setSearch] = useState('');
  const [filteredTipos, setFilteredTipos] = useState([]);
  const [editTipo, setEditTipo] = useState(null);
  const [newDescripcion, setNewDescripcion] = useState('');
  const [newTipoId, setNewTipoId] = useState('');
  const [newTipoDescripcion, setNewTipoDescripcion] = useState('');

  useEffect(() => {
    // Fetch all tipos from the backend
    axios.get('http://localhost:3000/tipos')
      .then(response => {
        setTipos(response.data);
        setFilteredTipos(response.data);
      })
      .catch(error => {
        console.error('Error fetching tipos:', error);
      });
  }, []);

  useEffect(() => {
    // Filter tipos based on the search input
    setFilteredTipos(
      tipos.filter(tipo => 
        tipo.descripcion.toLowerCase().includes(search.toLowerCase())
      )
    );
  }, [search, tipos]);

  const handleEdit = (tipo) => {
    setEditTipo(tipo);
    setNewDescripcion(tipo.descripcion);
  };

  const handleSave = () => {
    axios.put(`http://localhost:3000/tipos/${editTipo.id}`, { descripcion: newDescripcion })
      .then(response => {
        setTipos(tipos.map(tipo => tipo.id === editTipo.id ? response.data : tipo));
        setFilteredTipos(filteredTipos.map(tipo => tipo.id === editTipo.id ? response.data : tipo));
        setEditTipo(null);
        setNewDescripcion('');
      })
      .catch(error => {
        console.error('Error updating tipo:', error);
        alert(`Error al actualizar el registro: ${error.message}`);
      });
  };

  const handleDelete = (id) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este registro?')) {
        axios.delete(`http://localhost:3000/tipos/${id}`)
     // axios.delete(`/tipos/${id}`)
        .then(() => {
          setTipos(tipos.filter(tipo => tipo.id !== id));
          setFilteredTipos(filteredTipos.filter(tipo => tipo.id !== id));
        })
        .catch(error => {
          console.error('Error deleting tipo:', error);
          alert(`Error al eliminar el registro: ${error.message}`);
        });
    }
  };

  const handleAdd = () => {
    if (newTipoId.trim() === '' || newTipoDescripcion.trim() === '') {
      alert('El ID y la descripción no pueden estar vacíos.');
      return;
    }
  
    // Convertimos el ID a un número
    const numericId = parseInt(newTipoId, 10);
  
    if (isNaN(numericId)) {
      alert('El ID debe ser un número válido.');
      return;
    }
  
    axios.post('http://localhost:3000/tipos', { id: numericId, descripcion: newTipoDescripcion })
      .then(response => {
        setTipos([...tipos, response.data]);
        setFilteredTipos([...filteredTipos, response.data]);
        setNewTipoId('');
        setNewTipoDescripcion('');
      })
      .catch(error => {
        console.error('Error adding new tipo:', error);
        alert(`Error al grabar el registro: ${error.response?.data?.message || error.message}`);
      });
  };
  

  return (
    <div style={{ padding: '20px' }}>
      <h1>Lista de Tipos</h1>
      <input
        type="text"
        placeholder="Buscar por descripción"
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ marginBottom: '20px', padding: '10px', width: '100%' }}
      />

      <div style={{ marginBottom: '20px' }}>
        <input
          type="text"
          placeholder="Nuevo ID"
          value={newTipoId}
          onChange={e => setNewTipoId(e.target.value)}
          style={{ padding: '10px', marginRight: '10px' }}
        />
        <input
          type="text"
          placeholder="Nueva descripción"
          value={newTipoDescripcion}
          onChange={e => setNewTipoDescripcion(e.target.value)}
          style={{ padding: '10px', marginRight: '10px' }}
        />
        <button onClick={handleAdd} style={{ padding: '10px', backgroundColor: 'green', color: 'white', border: 'none', borderRadius: '5px' }}>
          ➕ Grabar
        </button>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ border: '1px solid black', padding: '10px' }}>ID</th>
            <th style={{ border: '1px solid black', padding: '10px' }}>Descripción</th>
            <th style={{ border: '1px solid black', padding: '10px' }}>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {filteredTipos.map(tipo => (
            <tr key={tipo.id}>
              <td style={{ border: '1px solid black', padding: '10px', textAlign: 'center' }}>{tipo.id}</td>
              <td style={{ border: '1px solid black', padding: '10px' }}>
                {editTipo && editTipo.id === tipo.id ? (
                  <input
                    type="text"
                    value={newDescripcion}
                    onChange={e => setNewDescripcion(e.target.value)}
                  />
                ) : (
                  tipo.descripcion
                )}
              </td>
              <td style={{ border: '1px solid black', padding: '10px', textAlign: 'center' }}>
                {editTipo && editTipo.id === tipo.id ? (
                  <button onClick={handleSave} style={{ marginRight: '10px' }}>💾</button>
                ) : (
                  <button onClick={() => handleEdit(tipo)} style={{ marginRight: '10px' }}>✏️</button>
                )}
                <button onClick={() => handleDelete(tipo.id)} style={{ color: 'red' }}>
                  🗑️
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TipoList;
*/